/**
 * Kono internal: the "lower price option" on an RFQ. A vendor answered after
 * the quote was generated (maybe after the customer got it) with a better
 * price for a quoted line, or with an item the quote could not cover. The
 * operator sees what beats what, thanks the vendor, lets the system write
 * the customer email, edits it, and approves: the next quote version is
 * created and the email waits in the Customer email panel to be sent.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BetterPrice,
  BetterPriceView,
  approveBetterPrice,
  dismissBetterPrice,
  fetchBetterPrice,
  proposeBetterPriceEmail,
} from "./api";

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : "");

export const money = (value: number | null | undefined, currency = "USD"): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  const amount = value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === "USD" ? `$${amount}` : `${amount} ${currency}`;
};

const STATUS_LABEL: Record<BetterPrice["status"], string> = {
  open: "Lower price option",
  revised: "Lower price applied",
  dismissed: "Lower price option dismissed",
};

const STATUS_TONE: Record<BetterPrice["status"], string> = {
  open: "border-amber-300 bg-amber-50",
  revised: "border-green-200 bg-green-50",
  dismissed: "border-gray-200 bg-gray-50",
};

type Props = {
  rfqId: string;
  /** The record as the RFQ came loaded; the panel refreshes it itself. */
  initial?: BetterPrice | null;
  /** Bumped by the workspace when something about the RFQ changed. */
  refreshKey?: number;
  /** Open the vendor reply panel for a request (to thank a late vendor). */
  onThankVendor?: (requestId: string) => void;
  /** After approve: the RFQ has a new quote and a prepared email. */
  onRevised?: () => void;
  onError?: (message: string) => void;
};

export const BetterPricePanel = ({ rfqId, initial, refreshKey = 0, onThankVendor, onRevised, onError }: Props) => {
  const [view, setView] = useState<BetterPriceView | null>(initial ? { betterPrice: initial, quote: null, autoRevise: false } : null);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<"" | "propose" | "approve" | "dismiss">("");
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState<string>("");

  const fail = useCallback((error: unknown) => onError?.(error instanceof Error ? error.message : String(error)), [onError]);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchBetterPrice(rfqId);
      setView(next);
      if (next.betterPrice?.proposal) {
        setSubject((cur) => cur || next.betterPrice!.proposal!.subject);
        setText((cur) => cur || next.betterPrice!.proposal!.text);
      }
    } catch (error) {
      fail(error);
    }
  }, [rfqId, fail]);

  useEffect(() => {
    setSubject("");
    setText("");
    setDone("");
    void refresh();
  }, [refresh, refreshKey]);

  const record = view?.betterPrice;
  if (!record) return null;

  const propose = async () => {
    setBusy("propose");
    try {
      const { proposal } = await proposeBetterPriceEmail(rfqId);
      setSubject(proposal.subject);
      setText(proposal.text);
      await refresh();
    } catch (error) {
      fail(error);
    } finally {
      setBusy("");
    }
  };

  const approve = async () => {
    const summary = record.customerHadQuote
      ? `Create the next version of ${record.quoteReference} with the better items and hold the customer email in the Customer email panel? Nothing is sent to the customer until you press Send there.`
      : `Replace ${record.quoteReference} with the better items? The customer has not received this quote yet, so it keeps its reference and the standard quote email is prepared.`;
    if (!window.confirm(summary)) return;
    setBusy("approve");
    try {
      const result = await approveBetterPrice(rfqId, subject.trim() && text.trim() ? { subject: subject.trim(), text: text.trim() } : null);
      setDone(
        result.revised
          ? `Quote ${result.quote.reference} created (${result.changes.join("; ")}). The customer email is ready in the Customer email panel below.`
          : `Quote ${result.quote.reference} updated in place (${result.changes.join("; ")}). The quote email is ready in the Customer email panel below.`,
      );
      await refresh();
      onRevised?.();
    } catch (error) {
      fail(error);
    } finally {
      setBusy("");
    }
  };

  const dismiss = async () => {
    const reason = window.prompt(`Keep ${record.quoteReference} as it is? Say why (optional):`, "");
    if (reason === null) return;
    setBusy("dismiss");
    try {
      await dismissBetterPrice(rfqId, reason);
      await refresh();
    } catch (error) {
      fail(error);
    } finally {
      setBusy("");
    }
  };

  const lateVendors = record.vendors.filter((v) => v.late);
  const vendorNames = record.vendors
    .map((v) => v.supplierName)
    .filter((name, i, all) => all.indexOf(name) === i)
    .join(", ");

  return (
    <div className={`mt-4 rounded-xl border p-4 text-sm text-gray-800 ${STATUS_TONE[record.status]}`} data-testid="better-price-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${record.status === "open" ? "bg-amber-200 text-amber-900" : record.status === "revised" ? "bg-green-200 text-green-900" : "bg-gray-200 text-gray-700"}`}>
            {STATUS_LABEL[record.status]}
          </span>
          <span className="font-medium">
            {record.savings > 0 ? `${money(record.savings, record.currency)} less` : `${record.lines.length} line${record.lines.length === 1 ? "" : "s"} now available`} on{" "}
            <Link className="text-blue-700 hover:underline" to={`/quotes/${record.quoteId}`}>
              {record.quoteReference}
            </Link>
          </span>
          <span className="text-xs text-gray-600" title={`Detected ${when(record.detectedAt)}; last change ${when(record.updatedAt)}`}>
            from {vendorNames}, {when(record.detectedAt)}
          </span>
        </div>
        <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => setOpen((o) => !o)}>
          {open ? "hide" : "show"}
        </button>
      </div>

      {open && (
        <>
          <p className="mt-2 text-xs text-gray-700">
            {record.customerHadQuote
              ? `${vendorNames} answered after quote ${record.quoteReference} went to the customer${lateVendors.length ? " (after the RFQ closed)" : ""}. Approving creates the next version (B, C, D...) with the better items and holds the email below for you to send.`
              : `${vendorNames} answered after quote ${record.quoteReference} was generated, before the customer received it. Approving replaces the quote in place and prepares the standard quote email.`}
            {view?.autoRevise === false && record.status === "open" && " Automatic revision is off in RFQ Configuration, so this needs your approval."}
          </p>

          <table className="mt-3 w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1 pr-2 font-medium">Requested</th>
                <th className="py-1 pr-2 font-medium">Quoted</th>
                <th className="py-1 pr-2 font-medium">Better option</th>
                <th className="py-1 text-right font-medium">Saving</th>
              </tr>
            </thead>
            <tbody>
              {record.lines.map((l) => (
                <tr key={l.lineId} className="border-t border-gray-200/70 align-top">
                  <td className="py-1 pr-2">{l.requested}</td>
                  <td className="py-1 pr-2 text-gray-700">
                    {l.kind === "cheaper" ? (
                      <>
                        {money(l.quotedLineAmount, l.currency)}
                        {l.quotedSupplierName ? ` from ${l.quotedSupplierName}` : ""}
                        {l.quotedSummary ? <div className="text-gray-500">{l.quotedSummary}</div> : null}
                      </>
                    ) : (
                      <span className="text-gray-500">not on the quote</span>
                    )}
                  </td>
                  <td className="py-1 pr-2">
                    <span className="font-medium">{money(l.newLineAmount, l.currency)}</span> from {l.newSupplierName}
                    {l.newUnitPrice !== null ? <span className="text-gray-500"> ({money(l.newUnitPrice, l.currency)}{l.newPriceUnit ? ` / ${l.newPriceUnit}` : ""})</span> : null}
                    <div className="text-gray-500">{l.newSummary}</div>
                  </td>
                  <td className="py-1 text-right font-medium text-green-800">{l.kind === "cheaper" ? money(l.savings, l.currency) : "new line"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {record.status === "open" && (
            <div className="mt-3 flex flex-col gap-3">
              {lateVendors.length > 0 && onThankVendor && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-gray-600">Thank the vendor for quoting after the RFQ closed:</span>
                  {lateVendors.map((v) => (
                    <button
                      key={v.requestId}
                      type="button"
                      className="rounded border border-blue-300 bg-white px-2 py-0.5 font-medium text-blue-800 hover:bg-blue-50"
                      onClick={() => onThankVendor(v.requestId)}
                    >
                      Reply to {v.supplierName}
                    </button>
                  ))}
                </div>
              )}

              {record.customerHadQuote && (
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-[#00062e]">Customer email</div>
                    <button
                      type="button"
                      className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                      disabled={busy !== ""}
                      onClick={propose}
                      title="The system writes the email: we kept working on your request and got a better price. Edit it before approving."
                    >
                      {busy === "propose" ? "Writing..." : record.proposal || text ? "Write it again" : "Write the customer email"}
                    </button>
                  </div>
                  {record.proposal && (
                    <div className="mt-1 text-xs text-gray-500">
                      Written {when(record.proposal.at)} ({record.proposal.model}); to {record.proposal.to}
                      {record.proposal.cc.length ? `, cc ${record.proposal.cc.join(", ")}` : ""}
                    </div>
                  )}
                  {(text || record.proposal) && (
                    <div className="mt-2 flex flex-col gap-2">
                      <input
                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Subject"
                      />
                      <textarea
                        className="min-h-[220px] w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                      />
                      <p className="text-xs text-gray-500">The quote reference in the email is replaced by the new version's when you approve.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded bg-[#00062e] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a2150] disabled:opacity-50"
                  disabled={busy !== "" || (record.customerHadQuote && !text.trim())}
                  onClick={approve}
                  title={
                    record.customerHadQuote
                      ? text.trim()
                        ? "Create the next quote version and hold this email in the Customer email panel"
                        : "Write the customer email first"
                      : "Replace the quote with the better items and prepare the standard quote email"
                  }
                >
                  {busy === "approve" ? "Applying..." : record.customerHadQuote ? "Approve: new quote version" : "Approve: update the quote"}
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  disabled={busy !== ""}
                  onClick={dismiss}
                >
                  {busy === "dismiss" ? "..." : "Dismiss, keep the quote"}
                </button>
              </div>
            </div>
          )}

          {done && <p className="mt-3 rounded border border-green-300 bg-white p-2 text-xs text-green-800">{done}</p>}

          {record.status === "revised" && record.revisedQuoteId && (
            <p className="mt-3 text-xs text-gray-700">
              Applied {when(record.revisedAt)} by {record.revisedBy}:{" "}
              <Link className="font-medium text-blue-700 hover:underline" to={`/quotes/${record.revisedQuoteId}`}>
                {record.revisedQuoteReference}
              </Link>
              . The customer email is in the Customer email panel below.
            </p>
          )}
          {record.status === "dismissed" && (
            <p className="mt-3 text-xs text-gray-700">
              Dismissed {when(record.dismissedAt)} by {record.dismissedBy}
              {record.dismissReason ? `: ${record.dismissReason}` : ""}.
            </p>
          )}
        </>
      )}
    </div>
  );
};
